import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
  useTheme
} from "@mui/material";
import { CheckmarkCircle24Px, LeafIcon } from "@qubit/autoparts";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { useAppDispatch, useAppSelector } from "~/app/store";
import MultiPort from "~/features/autostoreBin/MultiPort";
import { addSanitizedBin } from "~/features/autostorePutaway/autostorePutaway.slice";
import { AlertInfo } from "~/features/inventory/components/AlertInfo";
import { getFirstDefinedValue } from "~/lib/helpers";

import { selectIsShortSideBin } from "~/redux/selectors/workstationsSelectors";
import { NextEmptyBinResponse, WorkstationSummaryDto } from "~/types/api";

type Props = {
  isOpen: boolean;
  onClose: (isChangeSuggestedBinModalOpen: boolean) => void;
  onConfirm: (bin: NextEmptyBinResponse, compartment: number) => void;
  /** adds a "Cancel" button to the bottom of modal, and disables onClose from firing by clicking out of the modal */
  onCancel?: () => void;
  cancelDisabled?: boolean;
  workstation: WorkstationSummaryDto | null;
  currentSelectedBin: NextEmptyBinResponse | null;
  currentSelectedCompartment: number | null;
  nextEmptyBinByPort: {
    [portId: number]: NextEmptyBinResponse | null;
  };
  newSelectedCompartment?: number | null;
  setNewSelectedCompartment?: (compartment: number | null) => void;
  allowSelectingCompartmentsWithoutInventoryOnly?: boolean;
  /** The title in the header of the modal */
  modalTitle?: string;
  /** If true will show compartment selection. If false will only show bin outline. */
  allowCompartmentSelection?: boolean;
};

function ChangeSuggestedBinModal(props: Props) {
  const {
    isOpen,
    onClose,
    nextEmptyBinByPort,
    workstation,
    currentSelectedBin,
    currentSelectedCompartment,
    onConfirm,
    newSelectedCompartment: newSelectedCompartmentFromParent,
    setNewSelectedCompartment: setNewSelectedCompartmentFromParent,
    onCancel,
    cancelDisabled,
    allowSelectingCompartmentsWithoutInventoryOnly = true,
    modalTitle,
    allowCompartmentSelection = true
  } = props;
  const { t } = useTranslation();
  const { palette } = useTheme();
  const dispatch = useAppDispatch();
  const selectedVariant = useAppSelector(
    (state) => state.inventory.selectedVariant
  );
  const sanitizedBins = useAppSelector(
    (state) => state.autostorePutaway.sanitizedBins
  );
  const [newSelectedBin, setNewSelectedBin] =
    useState<NextEmptyBinResponse | null>(null);
  const [newSelectedCompartmentFromState, setNewSelectedCompartmentFromState] =
    useState<number | null>(null);
  const isShortSideBin = useAppSelector(selectIsShortSideBin);
  // Induction V1 and V2 need the new compartment state in the parent component
  // while autostore-inventory uses the state in this component
  const newSelectedCompartment =
    newSelectedCompartmentFromParent ?? newSelectedCompartmentFromState;
  const setNewSelectedCompartment =
    setNewSelectedCompartmentFromParent || setNewSelectedCompartmentFromState;

  const getIsConfirmButtonDisabled = () => {
    const newSelectionHasBeenMade =
      newSelectedBin &&
      (newSelectedCompartment || newSelectedCompartment === 0);
    const selectedBinId = newSelectedBin?.openBinResponse.binId;
    const isOriginalBinSelected =
      selectedBinId === currentSelectedBin?.openBinResponse.binId;
    const isOriginalCompartmentSelected =
      newSelectedCompartment === currentSelectedCompartment;
    const isBinSanitized =
      selectedBinId && sanitizedBins.includes(selectedBinId);
    const requiresSanitizedTargetBin = selectedVariant?.isSanitizeTargetBin;
    const sanitizationRequirementsMet =
      !requiresSanitizedTargetBin ||
      (requiresSanitizedTargetBin && isBinSanitized);
    if (allowCompartmentSelection) {
      return (
        !newSelectionHasBeenMade ||
        (isOriginalBinSelected && isOriginalCompartmentSelected) ||
        !sanitizationRequirementsMet
      );
    } else {
      return (
        !newSelectedBin || isOriginalBinSelected || !sanitizationRequirementsMet
      );
    }
  };

  const renderSanitizeButton = (bin: NextEmptyBinResponse) => {
    const selectedBin =
      bin.openBinResponse.binId === newSelectedBin?.openBinResponse.binId;
    if (!selectedBin || sanitizedBins.includes(bin.openBinResponse.binId)) {
      return null;
    }
    return (
      <Button
        variant="contained"
        onClick={() => dispatch(addSanitizedBin(bin.openBinResponse.binId))}
        sx={{
          position: "absolute",
          bottom: 0,
          left: 0,
          right: 0,
          top: 0,
          width: "200px",
          maxWidth: "100%",
          alignSelf: "center",
          justifySelf: "center"
        }}
      >
        {t("sanitize")}
      </Button>
    );
  };

  const renderSanitizedfIndicator = (binId: number) => {
    if (sanitizedBins.includes(binId)) {
      return (
        <Stack direction="row" mt={1} spacing={1} justifyContent="center">
          <CheckmarkCircle24Px fill={palette.success.dark} />
          <Typography>{t("sanitized")}</Typography>
        </Stack>
      );
    }
    return <Box height={32} />;
  };

  const defaultModalTitle = selectedVariant?.isSanitizeTargetBin
    ? t("wipe bin")
    : t("select new compartment");

  return (
    <Dialog
      open={isOpen}
      onClose={() => {
        setNewSelectedBin(null);
        setNewSelectedCompartment(null);
        if (!onCancel) onClose(false);
      }}
      fullWidth
      maxWidth="xl"
    >
      <DialogTitle>{modalTitle || defaultModalTitle}</DialogTitle>
      <DialogContent
        sx={{
          margin: "30px 30px 0",
          display: "flex",
          justifyContent: "center"
        }}
      >
        <Stack direction="column" alignItems="center" width="100%">
          {selectedVariant?.isSanitizeTargetBin && (
            <AlertInfo
              message={t("this item requires a sanitized bin")}
              type="sanitize"
              icon={<LeafIcon sx={{ color: palette.infoPurple.dark }} />}
            />
          )}
          <MultiPort
            nextEmptyBinByPort={nextEmptyBinByPort}
            workstation={workstation}
            renderBinButton={
              selectedVariant?.isSanitizeTargetBin
                ? renderSanitizeButton
                : undefined
            }
            renderBelowBinElement={renderSanitizedfIndicator}
            onBinClick={(compartment: number | undefined) => {
              if (compartment || compartment === 0)
                setNewSelectedCompartment(compartment);
            }}
            setSelectedBinCallback={(bin: NextEmptyBinResponse) =>
              setNewSelectedBin(bin)
            }
            selectedCompartment={
              allowCompartmentSelection
                ? getFirstDefinedValue(
                    [newSelectedCompartment, currentSelectedCompartment],
                    undefined
                  )
                : undefined
            }
            currentSelectedBinId={getFirstDefinedValue(
              [
                newSelectedBin?.openBinResponse.binId,
                currentSelectedBin?.openBinResponse.binId
              ],
              undefined
            )}
            allowSelectingCompartmentsWithoutInventoryOnly={
              allowSelectingCompartmentsWithoutInventoryOnly
            }
            isShortSideBin={isShortSideBin}
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ justifyContent: "center", padding: "30px" }}>
        {onCancel && (
          <Button
            size="large"
            variant="contained"
            color="error"
            onClick={() => {
              onCancel();
            }}
            disabled={cancelDisabled}
            sx={{
              fontWeight: "normal"
            }}
          >
            {t("cancel")}
          </Button>
        )}
        <Button
          size="large"
          variant="contained"
          disabled={getIsConfirmButtonDisabled()}
          onClick={() => {
            const selectedBin = newSelectedBin || currentSelectedBin;
            const selectedCompartment = getFirstDefinedValue(
              [newSelectedCompartment, currentSelectedCompartment],
              null
            );
            if (selectedBin && selectedCompartment !== null) {
              onConfirm(selectedBin, selectedCompartment);
              setNewSelectedBin(null);
            }
            onClose(false);
          }}
          sx={{
            fontWeight: "normal"
          }}
        >
          {t("confirm")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default ChangeSuggestedBinModal;
